// src/redux/sagas/siteContentSaga.ts
import { call,fork, put, takeEvery, select } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { request } from '../../api/request';
import ENDPOINTS from '../../api/endpoints';
import { parseApiResponse } from '../../utils/jsonParser';
import { SiteContent } from '../../types/siteContent';
import { RootState } from '../slices/rootReducer';
import {
  updateSiteContentRequest,
  updateSiteContentSuccess,
  updateSiteContentFailure,
  publishSiteContentRequest,
  publishSiteContentSuccess,
  publishSiteContentFailure,
  fetchSiteContentRequest,
  fetchSiteContentSuccess,
  fetchSiteContentFailure,
} from '../slices/siteContentSlice';
import { setTheme } from '../slices/themeSlice';
import { extractThemeFromSiteContent } from '../../utils/themeExtractor';
import { fetchTpnConfigRequest } from '../slices/tpnConfigSlice';
import { fetchTableAvailabilityRequest } from '../slices/tableAvailabilitySlice';
// import defaultSiteWidgets from '../../data/defaultSiteWidgets.json';
import { getDynamicHomeWidgets, processWidgetsSafely } from '../../utils/widgetUtils';
import { getTemplateData } from '../../data/templateData';
import { shouldSkipApiCalls } from '../../utils/appConfig';
import { getTemplateIdFromUrl } from '../../utils/pathUtils';
import { isEmpty } from '../../utils/emptyStateUtils';

// Type for request config
interface RequestConfig {
  method?: string;
  url: string;
  data?: unknown;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  base?: string;
  requiresAuth?: boolean;
  requiresRestaurantId?: boolean;
  timeout?: number;
  contentType?: string;
  responseType?: 'json' | 'text' | 'blob' | 'arraybuffer' | 'document' | 'stream';
}

// Helper function to extract unique widget types from nav and home widgets
// function extractWidgetTypes(navWidgets: any[], homeWidgets: any[]): string[] {
//   const allWidgets = [...navWidgets, ...homeWidgets];
//   const widgetTypes = allWidgets
//     .filter(widget => widget && widget.type)
//     .map(widget => widget.type);
  
//   // Return unique widget types
//   return [...new Set(widgetTypes)];
// }

function* fetchSiteContentSaga(): Generator<any, void, any> {
  try {
    // Check if we should skip API calls (preview mode)
    const skipApiCalls = shouldSkipApiCalls();
    
    let parsedData: SiteContent;
    
    if (skipApiCalls) {
      // Preview mode only - load template data directly without API calls
      const templateId = getTemplateIdFromUrl();
      const templateData = getTemplateData(templateId);
      
      // Structure the data to match SiteContent interface
      parsedData = {
        widgets: templateData?.widgets || []
      } as SiteContent;
    } else {
      // Normal mode - make API calls
      const restaurantId: string | undefined = yield select((state: RootState) => state.restaurant.selectedRestaurant?.restaurant_id);
      
      const url = `${ENDPOINTS.SITE_CONTENT_VIEW_API_PATH}?folder_path=v1`;
      const requestConfig: RequestConfig = {
        method: 'GET',
        url,
        requiresRestaurantId: false,
        headers: {
          ...(restaurantId && { restaurantid: restaurantId }) 
        },
      };
      const response: AxiosResponse<any> = yield call(request, requestConfig);
      
      parsedData = parseApiResponse<SiteContent>(response?.data);
    }
    
    // Only process widgets if parsedData has valid content
    if (parsedData && !isEmpty(parsedData.widgets)) {
      // Get current site content to preserve existing widget data (like combo/menu data)
      const currentSiteContent: SiteContent | null = yield select((state: RootState) => state.siteContent.data);
      
      // Process dynamic widgets after fallback
      const safeWidgets = processWidgetsSafely(parsedData.widgets);
      const { navWidgets, homeWidgets, menuWidgetTypes } = getDynamicHomeWidgets(safeWidgets);
      
      // Preserve existing widget data if available (to maintain combo/menu data from useWidgetDataUpdater)
      if (currentSiteContent?.homeWidgets) {
        // Create a map of existing widgets by type for quick lookup
        const existingWidgetMap = new Map();
        currentSiteContent.homeWidgets.forEach((widget: any, index: number) => {
          existingWidgetMap.set(`${widget.type}-${index}`, widget);
        });
        
        // Preserve data from existing widgets where possible
        homeWidgets.forEach((newWidget: any, index: number) => {
          const existingWidget = existingWidgetMap.get(`${newWidget.type}-${index}`);
          if (existingWidget?.data) {
            // Only preserve populated data to avoid keeping stale empty arrays
            
            // Preserve combo data (only if it has items)
            if (existingWidget.data.combos && Array.isArray(existingWidget.data.combos) && existingWidget.data.combos.length > 0) {
              newWidget.data = { ...newWidget.data, combos: existingWidget.data.combos };
            }
            
            // Preserve menu data (only if it has items)
            if (existingWidget.data.menuData && Array.isArray(existingWidget.data.menuData) && existingWidget.data.menuData.length > 0) {
              newWidget.data = { ...newWidget.data, menuData: existingWidget.data.menuData };
            }
            
            // Preserve selected item IDs (only if it has items)
            if (existingWidget.data.selectedItemIds && Array.isArray(existingWidget.data.selectedItemIds) && existingWidget.data.selectedItemIds.length > 0) {
              newWidget.data = { ...newWidget.data, selectedItemIds: existingWidget.data.selectedItemIds };
            }
          }
        });
      }
      
      // Update parsedData with filtered widgets
      parsedData.homeWidgets = homeWidgets;
      parsedData.navWidgets = navWidgets;
      parsedData.menuWidgetTypes = menuWidgetTypes;
    }

    // Extract and dispatch theme data from JSON structure using clean extractor
    try {
      const extractedTheme = extractThemeFromSiteContent(parsedData);
      
      // Dispatch theme update with font support
      yield put(setTheme({
        fontKey: extractedTheme.fontKey,
        primaryColor: extractedTheme.primaryColor,
      }));
    } catch (themeError) {
      // Don't fail the entire saga if theme extraction fails
    }

    // Always dispatch success (even if no widget processing occurred)
    yield put(fetchSiteContentSuccess(parsedData as unknown as SiteContent));
    
    // Auto-trigger TPN config and table availability after site content loads
    yield put(fetchTpnConfigRequest());
    
    // Get today's date for table availability check
    const todayDate = new Date().toISOString().split('T')[0];
    yield put(fetchTableAvailabilityRequest({ date: todayDate }));
    
    // Preload required widgets for improved performance
    // try {
    //   const widgetTypes = extractWidgetTypes(navWidgets, homeWidgets);
    //   yield call(preloadRequiredWidgets, widgetTypes)
    // } catch (preloadError) {
    //   // Don't fail the entire saga if preloading fails
    // }
    
    // Home screen data fetching moved to HomePage component

  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch site content';
    yield put(fetchSiteContentFailure(errorMessage));
  }
}

function* updateSiteContentSaga(): Generator<any, void, any> {
  // Skip API calls if in preview mode
  if (shouldSkipApiCalls()) {
    return;
  }

  try {
    // Get current site content from Redux store
    const siteContent: SiteContent | null = yield select((state: RootState) => state.siteContent.data);
    const restaurantId: string | undefined = yield select((state: RootState) => state.restaurant.selectedRestaurant?.restaurant_id);
    
    if (!siteContent) {
      yield put(updateSiteContentFailure('No site content available to update'));
      return;
    }

    // Deep clone to avoid mutating Redux state
    const cleanedSiteContent = JSON.parse(JSON.stringify(siteContent));
    
    // Clear menu data for popular widgets and combo data for combo widgets
    cleanedSiteContent.widgets.forEach((widget: any) => {
      if (widget.type.toLowerCase().includes('popular')) {
        widget.data.menuData = [];
      }
      if (widget.type.toLowerCase().includes('combo')) {
        widget.data.combos = [];
      }
    });
    
    // Also clean homeWidgets if it exists
    if (cleanedSiteContent.homeWidgets && Array.isArray(cleanedSiteContent.homeWidgets)) {
      cleanedSiteContent.homeWidgets.forEach((widget: any) => {
        if (widget.type.toLowerCase().includes('popular')) {
          widget.data.menuData = [];
        }
        if (widget.type.toLowerCase().includes('combo')) {
          widget.data.combos = [];
        }
      });
    }

    // Step 1: Update the data via PUT request with proper payload structure
    const payload = {
      folder_name: "preview",
      site_content: JSON.stringify({
         ...cleanedSiteContent,
        widgets: cleanedSiteContent.widgets
      })
    };
    
    const updateRequestConfig: RequestConfig = {
      method: 'PUT',
      url: ENDPOINTS.SITE_CONTENT_UPDATE_API_PATH,
      data: payload,
      requiresRestaurantId: false,
      headers: {
        ...(restaurantId && { restaurantid: restaurantId }) 
      },
    };
    yield call(request, updateRequestConfig);
    yield put(updateSiteContentSuccess(siteContent as unknown as SiteContent));
    yield fork(function* () {
    yield put(publishSiteContentRequest());
    });
    // yield put(publishSiteContentRequest());
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to update site content';
    yield put(updateSiteContentFailure(errorMessage));
  }
}

function* publishSiteContentSaga(): Generator<any, void, any> {
  // Skip API calls if in preview mode
  if (shouldSkipApiCalls()) {
    return;
  }

  try {
    // Get restaurant ID from Redux state
    const restaurantId: string | undefined = yield select((state: RootState) => state.restaurant.selectedRestaurant?.restaurant_id);
    
    // Publish the content
    const publishRequestConfig: RequestConfig = {
      method: 'GET',
      url: ENDPOINTS.SITE_CONTENT_PUBLISH_API_PATH,
      requiresRestaurantId: false,
      headers: {
        ...(restaurantId && { restaurantid: restaurantId }) 
      },
    };
    yield call(request, publishRequestConfig);
    yield put(publishSiteContentSuccess());
    yield fork(function* () {
    yield call(fetchSiteContentSaga);
    });
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to publish site content';
    yield put(publishSiteContentFailure(errorMessage));
  }
}

export function* watchSiteContentSaga() {
  yield takeEvery(fetchSiteContentRequest.type, fetchSiteContentSaga);
  yield takeEvery(updateSiteContentRequest.type, updateSiteContentSaga);
  yield takeEvery(publishSiteContentRequest.type, publishSiteContentSaga);
}
